<template>
  <v-container class="my-5">
    <v-layout row wrap class="justify-center">
      <v-flex xs12>
        <v-card-title class="mb-4">
          <v-btn icon @click="$router.go(-1)" class="mr-3">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <span class="headline font-weight-bold">{{ $t('golf_reservations') }}</span>
          <v-spacer></v-spacer>
          <v-btn v-if="isStarter" color="primary" rounded elevation="2" @click="openDialog()">
            <v-icon left>mdi-plus</v-icon>
            {{ $t('new_reservation') }}
          </v-btn>
        </v-card-title>

        <v-tabs v-model="activeTab" background-color="transparent" color="primary" class="mb-4">
          <v-tab>
            <v-icon left>mdi-calendar</v-icon>
            {{ $t('calendar_view') }}
          </v-tab>
          <v-tab>
            <v-badge color="red" :content="pendingRequestsCount.toString()" :value="pendingRequestsCount > 0" overlap>
              <v-icon left>mdi-bell-ring</v-icon>
              {{ $t('requests') }}
            </v-badge>
          </v-tab>
          <v-tab>
            <v-badge color="red" :content="cancellationRequestsCount.toString()" :value="cancellationRequestsCount > 0"
              overlap>
              <v-icon left>mdi-cancel</v-icon>
              {{ $t('cancellations') }}
            </v-badge>
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeTab" class="transparent">
          <!-- Tab 1: Calendar View -->
          <v-tab-item>
            <v-card class="fill-height" elevation="0">
              <v-toolbar flat color="white" class="mb-2">
                <v-btn outlined class="mr-4" color="grey darken-2" @click="setToday">
                  {{ $t('today') }}
                </v-btn>
                <v-btn fab text small color="grey darken-2" @click="prev">
                  <v-icon small>mdi-chevron-left</v-icon>
                </v-btn>
                <v-btn fab text small color="grey darken-2" @click="next">
                  <v-icon small>mdi-chevron-right</v-icon>
                </v-btn>
                <v-toolbar-title v-if="$refs.calendar">
                  {{ $refs.calendar.title }}
                </v-toolbar-title>
                <v-spacer></v-spacer>
              </v-toolbar>
              <v-sheet height="700">
                <v-calendar ref="calendar" v-model="focus" color="primary" type="month" locale="es-mx"
                  @click:date="openDayDialog" @change="updateRange">
                  <template v-slot:day="{ date }">
                    <div class="d-flex justify-center align-center fill-height"
                      style="width: 100%; height: 100%; cursor: pointer; background-color: transparent;"
                      @click="openDayDialog({ date })" v-ripple>
                      <v-chip v-if="getReservationCount(date) > 0" color="primary" small class="font-weight-bold">
                        {{ getReservationCount(date) }}
                      </v-chip>
                    </div>
                  </template>
                </v-calendar>
              </v-sheet>
            </v-card>
          </v-tab-item>

          <!-- Tab 2: Requests View -->
          <v-tab-item>
            <v-card elevation="3" class="rounded-lg">
              <v-data-table :headers="headers" :items="pendingReservations" :loading="loadingRequests"
                class="elevation-0" :no-data-text="$t('no_records_yet')">
                <template #item.course="{ item }">
                  <v-chip small label color="blue lighten-5" text-color="blue darken-3">
                    {{ getCourseName(item.course) }}
                  </v-chip>
                </template>
                <template #item.invited_people="{ item }">
                  <div v-if="item.invited_people && item.invited_people.length > 0">
                    <v-chip v-for="(person, index) in item.invited_people" :key="index" x-small class="mr-1 mb-1">
                      {{ person }}
                    </v-chip>
                  </div>
                  <span v-else class="grey--text caption font-italic">Sin jugadores</span>
                </template>
                <template #item.hour="{ item }">
                  <v-icon small class="mr-1">mdi-clock-outline</v-icon>
                  <strong>{{ item.hour }}</strong>
                </template>
                <template #item.date="{ item }">
                  {{ formatDate(item.date) }}
                </template>
                <template #item.status="{ item }">
                  <v-chip :color="getStatusColor(item.status)" dark small>
                    {{ getStatusText(item.status) }}
                  </v-chip>
                </template>
                <template #item.display_user="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="32" color="primary" class="white--text mr-2">
                      <span class="caption">{{ getInitials(item.display_user) }}</span>
                    </v-avatar>
                    {{ item.display_user }}
                  </div>
                </template>
                <template #item.actions="{ item }">
                  <div class="d-flex">
                    <!-- Pending Actions -->
                    <v-tooltip bottom>
                      <template #activator="{ on, attrs }">
                        <v-btn icon small color="green" v-bind="attrs" v-on="on" @click="changeStatus(item, 2)">
                          <v-icon>mdi-check</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t('confirm_reservation') }}</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template #activator="{ on, attrs }">
                        <v-btn icon small color="red" v-bind="attrs" v-on="on" @click="changeStatus(item, 5)">
                          <v-icon>mdi-close</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t('reject_reservation') }}</span>
                    </v-tooltip>
                  </div>
                </template>
              </v-data-table>
            </v-card>
          </v-tab-item>

          <!-- Tab 3: Cancellations View -->
          <v-tab-item>
            <v-card elevation="3" class="rounded-lg">
              <v-data-table :headers="headers" :items="cancellationReservations" :loading="loadingRequests"
                class="elevation-0" :no-data-text="$t('no_records_yet')">
                <template #item.course="{ item }">
                  <v-chip small label color="blue lighten-5" text-color="blue darken-3">
                    {{ getCourseName(item.course) }}
                  </v-chip>
                </template>
                <template #item.invited_people="{ item }">
                  <div v-if="item.invited_people && item.invited_people.length > 0">
                    <v-chip v-for="(person, index) in item.invited_people" :key="index" x-small class="mr-1 mb-1">
                      {{ person }}
                    </v-chip>
                  </div>
                  <span v-else class="grey--text caption font-italic">Sin jugadores</span>
                </template>
                <template #item.hour="{ item }">
                  <v-icon small class="mr-1">mdi-clock-outline</v-icon>
                  <strong>{{ item.hour }}</strong>
                </template>
                <template #item.date="{ item }">
                  {{ formatDate(item.date) }}
                </template>
                <template #item.status="{ item }">
                  <v-chip :color="getStatusColor(item.status)" dark small>
                    {{ getStatusText(item.status) }}
                  </v-chip>
                </template>
                <template #item.display_user="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="32" color="primary" class="white--text mr-2">
                      <span class="caption">{{ getInitials(item.display_user) }}</span>
                    </v-avatar>
                    {{ item.display_user }}
                  </div>
                </template>
                <template #item.actions="{ item }">
                  <div class="d-flex">
                    <!-- Cancellation Requested Actions -->
                    <v-tooltip bottom>
                      <template #activator="{ on, attrs }">
                        <v-btn icon small color="green" v-bind="attrs" v-on="on"
                          @click="handleCancellationRequest(item, 'approve')">
                          <v-icon>mdi-check-all</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t('accept_cancellation') }}</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template #activator="{ on, attrs }">
                        <v-btn icon small color="orange" v-bind="attrs" v-on="on"
                          @click="handleCancellationRequest(item, 'reject')">
                          <v-icon>mdi-undo</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t('reject_cancellation') }}</span>
                    </v-tooltip>
                  </div>
                </template>
              </v-data-table>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </v-flex>
    </v-layout>

    <!-- Day Reservations Dialog -->
    <v-dialog v-model="dayDialog" max-width="1000px" scrollable>
      <v-card style="max-height: 90vh; display: flex; flex-direction: column;">
        <v-toolbar color="primary" dark flat dense style="flex: 0 0 auto;">
          <v-toolbar-title class="subtitle-1">
            <v-icon left>mdi-calendar-check</v-icon>
            {{ $t('reservations_for') }} {{ formatDate(filterDate) }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="dayDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-4" style="display: flex; flex-direction: column; flex: 1; overflow-y: auto;">
          <!-- Resumen de jugadores del día -->
          <v-card outlined class="mb-4 pa-3 flex-shrink-0" color="grey lighten-5">
            <div class="d-flex justify-space-around align-center flex-wrap">
              <div class="text-center px-4 py-2">
                <v-icon color="primary" class="mb-1">mdi-account-check</v-icon>
                <div class="text-h5 font-weight-bold primary--text">{{ residentPlayersCount }}</div>
                <div class="caption grey--text text--darken-1">{{ $t('residents') }}</div>
              </div>
              <v-divider vertical class="mx-2 hidden-xs-only"></v-divider>
              <div class="text-center px-4 py-2">
                <v-icon color="orange" class="mb-1">mdi-account-multiple</v-icon>
                <div class="text-h5 font-weight-bold orange--text">{{ guestPlayersCount }}</div>
                <div class="caption grey--text text--darken-1">{{ $t('guests') }}</div>
              </div>
              <v-divider vertical class="mx-2 hidden-xs-only"></v-divider>
              <div class="text-center px-4 py-2">
                <v-icon color="green" class="mb-1">mdi-account-group</v-icon>
                <div class="text-h5 font-weight-bold green--text">{{ totalPlayersCount }}</div>
                <div class="caption grey--text text--darken-1">{{ $t('total_players') }}</div>
              </div>
            </div>
          </v-card>

          <div class="d-flex justify-space-between align-center mb-4 flex-shrink-0">
            <v-text-field v-model="search" append-icon="mdi-magnify" :label="$t('search')" single-line hide-details
              dense outlined style="max-width: 300px;"></v-text-field>
            <v-btn v-if="isStarter" color="primary" @click="openDialog()">
              <v-icon left>mdi-plus</v-icon>
              {{ $t('new_reservation') }}
            </v-btn>
          </div>

          <div style="flex: 1; overflow-y: auto; min-height: 0; max-height: 100%;">
            <table style="width: 100%; border-collapse: collapse;">
              <thead style="position: sticky; top: 0; z-index: 10; background: white;">
                <tr>
                  <th class="text-center primary white--text pa-2" style="width: 100px; border: 1px solid #e0e0e0;">
                    <v-icon small color="white" class="mr-1">mdi-clock-outline</v-icon>
                    {{ $t('horario') || 'Horario' }}
                  </th>
                  <th class="text-center primary white--text pa-2" style="border: 1px solid #e0e0e0;">
                    <v-icon small color="white" class="mr-1">mdi-flag-variant</v-icon>
                    {{ $t('hole') || 'Hoyo' }} 1
                  </th>
                  <th class="text-center primary white--text pa-2" style="border: 1px solid #e0e0e0;">
                    <v-icon small color="white" class="mr-1">mdi-flag-variant</v-icon>
                    {{ $t('hole') || 'Hoyo' }} 10
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="time in timeSlots" :key="time" class="tee-row">
                  <td class="text-center font-weight-bold grey lighten-4">
                    {{ time }}
                    <div v-if="isWeekend" class="caption green--text font-weight-bold mt-1" style="line-height: 1;">
                      <small>R: {{ getRecessTime(time) }}</small>
                    </div>
                  </td>

                  <!-- Salida 1 -->
                  <td class="pa-1 cell-hover" style="vertical-align: top; min-width: 250px; position: relative;">
                    <div class="d-flex flex-column fill-height">
                      <div v-for="res in getReservationsBySlot(time, 1)" :key="res.id" class="mb-1">
                        <v-card outlined
                          class="pa-2 d-flex align-center justify-space-between transition-swing clickable-card"
                          :class="{ 'member-card': isMember(res), 'guest-card': !isMember(res), 'tournament-card': isTournamentReservation(res) }"
                          :color="getStatusColor(res.status) + ' lighten-5'" style="border-left: 4px solid;"
                          :style="{ borderLeftColor: isTournamentReservation(res) ? '#9c27b0' : getStatusColor(res.status) }" @click="showReservationDetails(res)">
                          <div class="d-flex flex-column overflow-hidden" style="width: 100%;">
                            <div class="d-flex align-center">
                              <v-avatar size="24" :color="isMember(res) ? 'primary' : 'orange'"
                                class="white--text mr-2">
                                <span class="caption" style="font-size: 10px;">{{ getInitials(res.display_user)
                                }}</span>
                              </v-avatar>
                              <div class="font-weight-bold caption text-truncate">
                                {{ res.display_user }}
                                <v-icon x-small :color="isMember(res) ? 'primary' : 'orange'" class="ml-1">
                                  {{ isMember(res) ? 'mdi-account-check' : 'mdi-account-arrow-right' }}
                                </v-icon>
                              </div>
                            </div>
                            <div class="d-flex align-center mt-1">
                              <v-chip x-small label outlined class="mr-1">
                                {{ res.invited_people ? res.invited_people.length : 0 }} jug.
                              </v-chip>
                              <v-chip x-small :color="getStatusColor(res.status)" dark class="mr-1">
                                {{ getStatusText(res.status) }}
                              </v-chip>
                              <v-chip v-if="isTournamentReservation(res)" x-small color="purple" dark>
                                <v-icon x-small left>mdi-tournament</v-icon>
                                Torneo
                              </v-chip>
                            </div>
                          </div>

                          <div class="d-flex ml-1">
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn icon x-small v-bind="attrs" v-on="on" @click.stop="editItem(res)"
                                  :disabled="isActionDisabled(res, 'edit')">
                                  <v-icon small>mdi-pencil</v-icon>
                                </v-btn>
                              </template>
                              <span>{{ $t('edit') }}</span>
                            </v-tooltip>
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn icon x-small color="red" v-bind="attrs" v-on="on"
                                  @click.stop="confirmCancel(res)" :disabled="isActionDisabled(res, 'cancel')">
                                  <v-icon small>mdi-delete</v-icon>
                                </v-btn>
                              </template>
                              <span>{{ $t('cancel_reservation') }}</span>
                            </v-tooltip>
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn icon x-small v-bind="attrs" v-on="on" @click.stop="confirmNoShow(res)"
                                  :disabled="isActionDisabled(res, 'no_show')">
                                  <v-icon small>mdi-account-cancel</v-icon>
                                </v-btn>
                              </template>
                              <span>{{ $t('mark_as_no_show') }}</span>
                            </v-tooltip>
                          </div>
                        </v-card>
                      </div>

                      <!-- Add Button -->
                      <div class="add-btn-container d-flex justify-center align-center"
                        style="min-height: 30px; opacity: 0.2; transition: opacity 0.2s;"
                        @click="openDialogWithTime(time, 1)">
                        <v-btn icon small color="primary">
                          <v-icon>mdi-plus</v-icon>
                        </v-btn>
                      </div>
                    </div>
                  </td>

                  <!-- Salida 10 -->
                  <td class="pa-1 cell-hover" style="vertical-align: top; min-width: 250px; position: relative;">
                    <div class="d-flex flex-column fill-height">
                      <div v-for="res in getReservationsBySlot(time, 10)" :key="res.id" class="mb-1">
                        <v-card outlined
                          class="pa-2 d-flex align-center justify-space-between transition-swing clickable-card"
                          :class="{ 'member-card': isMember(res), 'guest-card': !isMember(res), 'tournament-card': isTournamentReservation(res) }"
                          :color="getStatusColor(res.status) + ' lighten-5'" style="border-left: 4px solid;"
                          :style="{ borderLeftColor: isTournamentReservation(res) ? '#9c27b0' : getStatusColor(res.status) }" @click="showReservationDetails(res)">
                          <div class="d-flex flex-column overflow-hidden" style="width: 100%;">
                            <div class="d-flex align-center">
                              <v-avatar size="24" :color="isMember(res) ? 'primary' : 'orange'"
                                class="white--text mr-2">
                                <span class="caption" style="font-size: 10px;">{{ getInitials(res.display_user)
                                }}</span>
                              </v-avatar>
                              <div class="font-weight-bold caption text-truncate">
                                {{ res.display_user }}
                                <v-icon x-small :color="isMember(res) ? 'primary' : 'orange'" class="ml-1">
                                  {{ isMember(res) ? 'mdi-account-check' : 'mdi-account-arrow-right' }}
                                </v-icon>
                              </div>
                            </div>
                            <div class="d-flex align-center mt-1">
                              <v-chip x-small label outlined class="mr-1">
                                {{ res.invited_people ? res.invited_people.length : 0 }} jug.
                              </v-chip>
                              <v-chip x-small :color="getStatusColor(res.status)" dark class="mr-1">
                                {{ getStatusText(res.status) }}
                              </v-chip>
                              <v-chip v-if="isTournamentReservation(res)" x-small color="purple" dark>
                                <v-icon x-small left>mdi-tournament</v-icon>
                                Torneo
                              </v-chip>
                            </div>
                          </div>

                          <div class="d-flex ml-1">
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn icon x-small v-bind="attrs" v-on="on" @click.stop="editItem(res)"
                                  :disabled="isActionDisabled(res, 'edit')">
                                  <v-icon small>mdi-pencil</v-icon>
                                </v-btn>
                              </template>
                              <span>{{ $t('edit') }}</span>
                            </v-tooltip>
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn icon x-small color="red" v-bind="attrs" v-on="on"
                                  @click.stop="confirmCancel(res)" :disabled="isActionDisabled(res, 'cancel')">
                                  <v-icon small>mdi-delete</v-icon>
                                </v-btn>
                              </template>
                              <span>{{ $t('cancel_reservation') }}</span>
                            </v-tooltip>
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn icon x-small v-bind="attrs" v-on="on" @click.stop="confirmNoShow(res)"
                                  :disabled="isActionDisabled(res, 'no_show')">
                                  <v-icon small>mdi-account-cancel</v-icon>
                                </v-btn>
                              </template>
                              <span>{{ $t('mark_as_no_show') }}</span>
                            </v-tooltip>
                          </div>
                        </v-card>
                      </div>

                      <!-- Add Button -->
                      <div class="add-btn-container d-flex justify-center align-center"
                        style="min-height: 30px; opacity: 0.2; transition: opacity 0.2s;"
                        @click="openDialogWithTime(time, 10)">
                        <v-btn icon small color="primary">
                          <v-icon>mdi-plus</v-icon>
                        </v-btn>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Dialog Create/Edit -->
    <v-dialog v-model="dialog" max-width="700px" persistent>
      <v-card>
        <v-card-title class="primary white--text">
          <span class="headline">{{ formTitle }}</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="mt-4">
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-container grid-list-md class="pa-0">
              <!-- Main Details -->
              <v-subheader class="pl-0 font-weight-bold text-uppercase caption primary--text">
                {{ $t('reservation_details') }}
              </v-subheader>
              <!-- Limited edit mode alert -->
              <v-alert v-if="isLimitedEdit" type="info" dense outlined class="mb-3">
                {{ $t('limited_edit_mode') }}
              </v-alert>

              <v-row dense>
                <v-col cols="12" sm="6">
                  <div class="d-flex align-center">
                    <v-combobox v-model="selectedUser" :items="allUsers" :loading="loadingUsers || loadingCCSLPUsers"
                    :search-input="userSearch"
                    @update:search-input="val => { if (typeof val === 'string') userSearch = val }"
                    item-text="full_name" item-value="id" :label="$t('holder') + ' / ' + $t('guest')"
                    :rules="[rules.required]" outlined dense clearable :no-data-text="$t('no_users_found')"
                    prepend-inner-icon="mdi-account-search" hide-details="auto" return-object
                    :filter="filterUsers"
                    @change="onUserSelectionChange" :disabled="isLimitedEdit">
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ item.full_name }}
                          <v-chip v-if="item.is_moroso" x-small color="red" class="ml-2" dark>
                            MOROSO
                          </v-chip>
                          <v-chip v-if="item.type === 'ccslp_linked'" x-small color="blue" class="ml-2" dark>
                            SOCIO
                          </v-chip>
                        </v-list-item-title>
                        <v-list-item-subtitle>{{ item.displaySubtitle }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-combobox>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon small class="ml-1" v-bind="attrs" v-on="on" @click="sortByAction = !sortByAction"
                        :color="sortByAction ? 'primary' : 'grey'">
                        <v-icon>mdi-sort-numeric-ascending</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ sortByAction ? 'Ordenado por Acción' : 'Ordenado Alfabéticamente' }}</span>
                  </v-tooltip>
                  </div>
                </v-col>

                <v-col v-if="isGuestSelected()" cols="12" sm="6">
                  <v-select v-model="editedItem.entry_method" :items="entryMethodOptions()" item-text="text" item-value="value"
                    :label="$t('entry_method')" :rules="[rules.required]" outlined dense prepend-inner-icon="mdi-ticket-account"
                    :disabled="isLimitedEdit"></v-select>
                </v-col>

                <v-col cols="12" sm="6">
                  <v-menu v-model="menuDateForm" :close-on-content-click="false" :nudge-right="40"
                    transition="scale-transition" offset-y min-width="auto" :disabled="isLimitedEdit">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field v-model="editedItem.date" :label="$t('reservation_date')"
                        prepend-inner-icon="mdi-calendar" readonly v-bind="attrs" v-on="isLimitedEdit ? {} : on"
                        :rules="[rules.required]" outlined dense :disabled="isLimitedEdit"></v-text-field>
                    </template>
                    <v-date-picker v-model="editedItem.date" @input="menuDateForm = false" locale="es-mx"
                      :min="minDateAllowed"></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-menu ref="menuTime" v-model="menuTime" :close-on-content-click="false" :nudge-right="40"
                    :return-value.sync="editedItem.hour" transition="scale-transition" offset-y max-width="290px"
                    min-width="290px" :disabled="isLimitedEdit">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field v-model="editedItem.hour" :label="$t('reservation_time')"
                        prepend-inner-icon="mdi-clock-time-four-outline" readonly v-bind="attrs"
                        v-on="isLimitedEdit ? {} : on" :rules="[rules.required]" outlined dense
                        :disabled="isLimitedEdit"></v-text-field>
                    </template>
                    <v-time-picker v-if="menuTime" v-model="editedItem.hour" full-width format="24hr"
                      @click:minute="$refs.menuTime.save(editedItem.hour)"></v-time-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-select v-model="editedItem.start_hole" :items="[1, 10]"
                    :label="$t('start_hole') || 'Hoyo de Inicio'" :rules="[rules.required]" outlined dense
                    prepend-inner-icon="mdi-flag-variant" :disabled="isLimitedEdit"></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-select v-model="editedItem.hole_count" :items="[9, 18]"
                    :label="$t('hole_count') || 'Cantidad de Hoyos'" :rules="[rules.required]" outlined dense
                    prepend-inner-icon="mdi-counter" :disabled="isLimitedEdit"></v-select>
                </v-col>
                <!-- Status selector for limited edit mode -->
                <v-col v-if="isLimitedEdit" cols="12" sm="6">
                  <v-select v-model="editedItem.status" :items="statusOptions" item-text="text" item-value="value"
                    :label="$t('status')" outlined dense prepend-inner-icon="mdi-information"></v-select>
                </v-col>
              </v-row>

              <!-- Players Section -->
              <v-subheader class="pl-0 font-weight-bold text-uppercase caption primary--text mt-2">
                {{ $t('players') }} & {{ $t('caddie') }}
              </v-subheader>

              <!-- Main Player Row -->
              <v-row dense align="center" class="mb-2">
                <v-col cols="12" sm="6">
                  <v-text-field v-model="mainPlayerName" :label="$t('player') + ' 1'" outlined dense
                    prepend-inner-icon="mdi-account" hide-details
                    :readonly="!!(editedItem.user_id || editedItem.ccslp_user_id)">
                  </v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-select v-model="mainPlayerCaddie" :items="availableCaddies(mainPlayerCaddie)" item-text="name"
                    item-value="id" :label="$t('caddie')" outlined dense clearable prepend-inner-icon="mdi-account-tie"
                    hide-details @change="updateCaddieIds">
                    <template v-slot:append-outer>
                      <v-btn icon small color="primary" @click="openNewCaddieDialog(-1)">
                        <v-icon>mdi-plus</v-icon>
                      </v-btn>
                    </template>
                  </v-select>
                </v-col>
              </v-row>

              <!-- Additional Players Rows -->
              <v-row dense align="center" v-for="(player, index) in editedItem.invited_people" :key="index"
                class="mb-2">
                <v-col cols="12" sm="6">
                  <v-text-field v-model="editedItem.invited_people[index]" :label="$t('player') + ' ' + (index + 2)"
                    outlined dense prepend-inner-icon="mdi-account-group" hide-details>
                    <template v-slot:append-outer>
                      <v-btn icon small color="red" @click="removePlayer(index)">
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                    </template>
                  </v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-select v-model="guestCaddies[index]" :items="availableCaddies(guestCaddies[index])"
                    item-text="name" item-value="id" :label="$t('caddie')" outlined dense clearable
                    prepend-inner-icon="mdi-account-tie" hide-details @change="updateCaddieIds">
                    <template v-slot:append-outer>
                      <v-btn icon small color="primary" @click="openNewCaddieDialog(index)">
                        <v-icon>mdi-plus</v-icon>
                      </v-btn>
                    </template>
                  </v-select>
                </v-col>
              </v-row>

              <!-- Add Player Input -->
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-combobox v-model="newPlayerSelection" :items="allUsers"
                    :loading="loadingUsers || loadingCCSLPUsers" :search-input.sync="newPlayerSearch"
                    item-text="full_name" item-value="id" :label="$t('add_player')" outlined dense clearable
                    :no-data-text="$t('no_users_found')" prepend-inner-icon="mdi-account-plus" hide-details="auto"
                    return-object :disabled="editedItem.invited_people.length >= 5" @change="onPlayerSelectionChange"
                    :filter="filterUsers"
                    @keydown.enter.prevent="addPlayerFromInput">
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ item.full_name }}
                          <v-chip v-if="item.is_moroso" x-small color="red" class="ml-2" dark>
                            MOROSO
                          </v-chip>
                          <v-chip v-if="item.type === 'ccslp_linked'" x-small color="blue" class="ml-2" dark>
                            SOCIO
                          </v-chip>
                        </v-list-item-title>
                        <v-list-item-subtitle>{{ item.displaySubtitle }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                    <template v-slot:append>
                      <v-btn icon small color="primary" @click="addPlayerFromInput"
                        :disabled="!newPlayerSelection && !newPlayerSearch || editedItem.invited_people.length >= 5">
                        <v-icon>mdi-plus</v-icon>
                      </v-btn>
                    </template>
                  </v-combobox>
                </v-col>
              </v-row>

              <!-- Tournament Link Section -->
              <v-subheader class="pl-0 font-weight-bold text-uppercase caption primary--text mt-4">
                {{ $t('tournament') || 'Torneo' }}
              </v-subheader>

              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-checkbox
                    v-model="linkToTournament"
                    :label="$t('link_to_tournament') || '¿Vincular a un torneo?'"
                    dense hide-details
                    @change="onTournamentLinkChange">
                  </v-checkbox>
                </v-col>
              </v-row>

              <!-- Tournament Selector (shown if linking) -->
              <div v-if="linkToTournament" class="mt-4 pa-3 rounded blue lighten-5" style="border: 1px solid #90caf9;">
                <v-row dense class="mb-3">
                  <v-col cols="12">
                    <v-select
                      v-model="editedItem.tournament_id"
                      :items="availableTournaments"
                      item-text="name"
                      item-value="id"
                      :label="$t('select_tournament') || 'Seleccionar Torneo'"
                      outlined dense
                      :loading="loadingTournaments"
                      :rules="linkToTournament ? [rules.required] : []"
                      prepend-inner-icon="mdi-tournament"
                      @change="onTournamentSelected">
                      <template v-slot:item="{ item }">
                        <div>
                          <strong>{{ item.name }}</strong>
                          <div class="text-caption grey--text">
                            {{ formatDate(item.start_date) }} - {{ formatDate(item.end_date) }}
                          </div>
                        </div>
                      </template>
                    </v-select>
                  </v-col>
                </v-row>

                <!-- Registered Players in Tournament -->
                <div v-if="tournamentRegistrations.length > 0" class="mb-3">
                  <v-subheader class="pl-0 text-caption grey--text">
                    {{ $t('registered_players') || 'Jugadores Registrados' }} ({{ tournamentRegistrations.length }})
                  </v-subheader>
                  <v-list dense class="rounded" style="background-color: white;">
                    <v-list-item
                      v-for="reg in tournamentRegistrations"
                      :key="reg.id"
                      class="px-2">
                      <v-list-item-content>
                        <v-list-item-title class="font-weight-bold">
                          {{ reg.player_display }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          HC: {{ reg.player_handicap }} | {{ getRegistrationStatus(reg.status) }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </div>

                <v-alert v-else-if="editedItem.tournament_id" type="info" dense small>
                  {{ $t('no_registrations') || 'No hay jugadores registrados' }}
                </v-alert>
              </div>

              <!-- Recurrence Section -->
              <!-- <div v-if="editedIndex === -1 && (editedItem.user_id || editedItem.ccslp_user_id)" class="mt-4 pa-3 rounded grey lighten-5"
                style="border: 1px solid #e0e0e0;">
                <v-row align="center" dense>
                  <v-col cols="12" sm="4" class="py-0">
                    <v-checkbox v-model="editedItem.is_recurring" :label="$t('is_recurring')" dense hide-details
                      class="mt-0"></v-checkbox>
                  </v-col>
                  <template v-if="editedItem.is_recurring">
                    <v-col cols="12" sm="4" class="py-0">
                      <v-menu v-model="menuEndDateForm" :close-on-content-click="false" :nudge-right="40"
                        transition="scale-transition" offset-y min-width="auto">
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field v-model="editedItem.end_date" :label="$t('end_date')"
                            prepend-inner-icon="mdi-calendar-end" readonly v-bind="attrs" v-on="on"
                            :rules="[rules.required]" outlined dense hide-details></v-text-field>
                        </template>
                        <v-date-picker v-model="editedItem.end_date" @input="menuEndDateForm = false" locale="es-mx"
                          :min="editedItem.date"></v-date-picker>
                      </v-menu>
                    </v-col>
                    <v-col cols="12" sm="4" class="py-0">
                      <v-select v-model="editedItem.days_of_week" :items="daysOfWeekOptions" :label="$t('days_of_week')"
                        multiple chips small-chips :rules="[rules.required]" outlined dense hide-details
                        prepend-inner-icon="mdi-calendar-week"></v-select>
                    </v-col>
                  </template>
                </v-row>
              </div> -->
            </v-container>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="close">{{ $t('cancelar') }}</v-btn>
          <v-btn color="primary" @click="save" :disabled="!valid">{{ $t('guardar') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Cancel Confirmation Dialog -->
    <v-dialog v-model="dialogCancel" max-width="400px">
      <v-card>
        <v-card-title class="headline">{{ $t('cancel_reservation') }}</v-card-title>
        <v-card-text>{{ $t('confirm_cancel_reservation') }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="closeCancel">{{ $t('no') || 'No' }}</v-btn>
          <v-btn color="red darken-1" text @click="cancelItemConfirm">{{ $t('si') || 'Si' }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- No Show Confirmation Dialog -->
    <v-dialog v-model="dialogNoShow" max-width="400px">
      <v-card>
        <v-card-title class="headline">{{ $t('mark_as_no_show') }}</v-card-title>
        <v-card-text>{{ $t('confirm_no_show') }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="closeNoShow">{{ $t('no') || 'No' }}</v-btn>
          <v-btn color="red darken-1" text @click="noShowItemConfirm">{{ $t('si') || 'Si' }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Reservation Details Dialog -->
    <v-dialog v-model="detailsDialog" max-width="500px">
      <v-card v-if="selectedReservation">
        <v-toolbar :color="getStatusBaseColor(selectedReservation.status)" dark flat>
          <v-toolbar-title>{{ $t('reservation_details') || 'Detalles de Reservación' }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="detailsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pt-4">
          <v-list dense>
            <v-list-item>
              <v-list-item-icon><v-icon color="primary">mdi-calendar</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle>{{ $t('date') }}</v-list-item-subtitle>
                <v-list-item-title>{{ formatDate(selectedReservation.date) }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-icon><v-icon color="primary">mdi-clock-outline</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle>{{ $t('time') }}</v-list-item-subtitle>
                <v-list-item-title>{{ selectedReservation.hour }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item v-if="isTournamentReservation(selectedReservation)">
              <v-list-item-icon><v-icon color="purple">mdi-tournament</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle>{{ $t('tournament') }}</v-list-item-subtitle>
                <v-list-item-title>
                  <v-chip x-small color="purple" dark>
                    <v-icon x-small left>mdi-tournament</v-icon>
                    Reservación vinculada a un torneo
                  </v-chip>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-icon><v-icon color="primary">mdi-golf</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle>{{ $t('golf_court') }}</v-list-item-subtitle>
                <v-list-item-title>
                  {{ getCourseName(selectedReservation.course) }}
                  <AgreementBadge
                    v-if="selectedAgreement"
                    :agreement="selectedAgreement"
                    class="ml-2"
                  />
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-icon><v-icon color="primary">mdi-flag-variant</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle>{{ $t('start_hole') }}</v-list-item-subtitle>
                <v-list-item-title>{{ selectedReservation.start_hole }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-icon><v-icon color="primary">mdi-account</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle>{{ $t('holder') }}</v-list-item-subtitle>
                <v-list-item-title>{{ formatDisplayName(selectedReservation.display_user, true) }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-icon><v-icon color="primary">mdi-account-group</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle>{{ $t('players') }}</v-list-item-subtitle>
                <v-list-item-title>
                  <div v-if="selectedReservation.invited_people && selectedReservation.invited_people.length > 0"
                    class="chips-scroll-container">
                    <v-chip v-for="(person, index) in selectedReservation.invited_people" :key="index" small
                      class="mr-1 flex-shrink-0">
                      {{ formatDisplayName(person) }}
                    </v-chip>
                  </div>
                  <span v-else class="grey--text font-italic">{{ $t('no_players') || 'Sin jugadores' }}</span>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-icon><v-icon color="primary">mdi-account-tie</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle>{{ $t('caddies') || 'Caddies' }}</v-list-item-subtitle>
                <v-list-item-title>
                  <div v-if="selectedReservation.caddies && selectedReservation.caddies.length > 0"
                    class="chips-scroll-container">
                    <v-chip v-for="(caddie, index) in selectedReservation.caddies" :key="index" small
                      class="mr-1 flex-shrink-0" color="teal" outlined>
                      {{ typeof caddie === 'object' ? caddie.name : getCaddieName(caddie) }}
                    </v-chip>
                  </div>
                  <span v-else class="grey--text font-italic">{{ $t('no_caddies') || 'Sin caddies' }}</span>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-icon><v-icon color="primary">mdi-information</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle>{{ $t('status') }}</v-list-item-subtitle>
                <v-list-item-title>
                  <v-chip :color="getStatusColor(selectedReservation.status)" dark small>
                    {{ getStatusText(selectedReservation.status) }}
                  </v-chip>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions class="justify-center pb-4">
          <v-btn color="primary" @click="editItem(selectedReservation); detailsDialog = false"
            :disabled="isActionDisabled(selectedReservation, 'edit')">
            <v-icon left>mdi-pencil</v-icon> {{ $t('edit') }}
          </v-btn>
          <v-btn color="success" @click="openDownloadModal"
            :disabled="!selectedReservation">
            <v-icon left>mdi-download</v-icon> {{ $t('download_scorecard') }}
          </v-btn>
          <v-btn color="red" dark @click="confirmCancel(selectedReservation); detailsDialog = false"
            :disabled="isActionDisabled(selectedReservation, 'cancel')">
            <v-icon left>mdi-delete</v-icon> {{ $t('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- New Caddie Dialog -->
    <v-dialog v-model="dialogNewCaddie" max-width="400px">
      <v-card>
        <v-card-title class="subtitle-1 font-weight-bold">
          {{ $t('new_caddie') }}
        </v-card-title>
        <v-card-text class="pt-2">
          <v-form ref="formNewCaddie" v-model="validNewCaddie" lazy-validation>
            <v-text-field v-model="newCaddie.name" :label="$t('nombre')" :rules="[rules.required]" outlined
              dense></v-text-field>
            <v-select v-model="newCaddie.caddie_type" :items="caddieTypes" item-text="text" item-value="value"
              label="Tipo" :rules="[rules.required]" outlined dense></v-select>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="grey" @click="closeNewCaddieDialog">{{ $t('cancelar') }}</v-btn>
          <v-btn color="primary" @click="saveNewCaddie" :disabled="!validNewCaddie" :loading="loadingNewCaddie">
            {{ $t('guardar') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Download Scorecard Modal -->
    <DownloadScorecardModal
      :show="showDownloadModal"
      :reservation="selectedReservation"
      :agreement="selectedAgreement"
      @close="showDownloadModal = false"
    />

    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="10000" top>
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from 'axios'
import store from '@/store'
import AgreementBadge from './components/AgreementBadge.vue'
import DownloadScorecardModal from './components/DownloadScorecardModal.vue'

const API_BASE_URL = 'https://clubtest.intrare.app'

export default {
  name: 'GolfReservations',
  components: {
    AgreementBadge,
    DownloadScorecardModal
  },
  data: () => ({
    activeTab: 0,
    loading: false,
    loadingRequests: false,
    loadingUsers: false,
    loadingCCSLPUsers: false,
    dialog: false,
    dialogCancel: false,
    dialogNoShow: false,
    detailsDialog: false,
    showDownloadModal: false,
    selectedReservation: null,
    selectedAgreement: null,
    dayDialog: false,
    valid: true,
    menuDateForm: false,
    menuEndDateForm: false,
    menuTime: false,
    focus: new Date().toISOString().substr(0, 10),
    filterDate: new Date().toISOString().substr(0, 10),
    today: new Date().toISOString().substr(0, 10),
    search: '',
    userSearch: null,
    newPlayerSearch: null,
    courts: [],
    users: [],
    ccslpUsers: [],
    searchedCCSLPUsers: [], // Usuarios CCSLP resultado de búsqueda
    caddies: [],
    reservations: [],
    monthReservations: [],
    monthReservationsCache: {}, // caché: { 'YYYY-MM' → [] }
    pendingReservations: [],
    cancellationReservations: [],
    reservationEvents: [],
    itemToCancel: null,
    itemToNoShow: null,
    editedIndex: -1,
    userSearchTimeout: null,
    playerSearchTimeout: null,
    editedItem: {
      id: null,
      user_id: null,
      ccslp_user_id: null,
      guest_name: '',
      entry_method: null,
      course_id: '',
      caddie_ids: [],
      date: new Date().toISOString().substr(0, 10),
      hour: null,
      start_hole: 1,
      hole_count: 18,
      invited_people: [],
      invited_users: {},
      is_recurring: false,
      end_date: null,
      days_of_week: [],
      tournament_id: null
    },
    defaultItem: {
      id: null,
      user_id: null,
      ccslp_user_id: null,
      guest_name: '',
      entry_method: null,
      course_id: '',
      caddie_ids: [],
      date: new Date().toISOString().substr(0, 10),
      hour: null,
      start_hole: 1,
      hole_count: 18,
      invited_people: [],
      invited_users: {},
      is_recurring: false,
      end_date: null,
      days_of_week: [],
      tournament_id: null
    },
    reservationType: 'resident',
    daysOfWeekOptions: [
      { text: 'Lunes', value: 0 },
      { text: 'Martes', value: 1 },
      { text: 'Miércoles', value: 2 },
      { text: 'Jueves', value: 3 },
      { text: 'Viernes', value: 4 },
      { text: 'Sábado', value: 5 },
      { text: 'Domingo', value: 6 }
    ],
    snackbar: false,
    snackbarText: '',
    snackbarColor: 'success',
    mainPlayerCaddie: null,
    mainPlayerName: '',
    guestCaddies: [],
    newPlayerName: '',
    selectedUser: null,
    newPlayerSelection: null,
    dialogNewCaddie: false,
    validNewCaddie: true,
    loadingNewCaddie: false,
    newCaddie: {
      name: '',
      caddie_type: null
    },
    caddieCreationContext: null,
    sortByAction: false,
    linkToTournament: false,
    availableTournaments: [],
    loadingTournaments: false,
    tournamentRegistrations: []
  }),

  watch: {
    'editedItem.start_hole': function (newValue) {
      const course = this.getCourseByStartHole(newValue)
      if (course) {
        this.editedItem.course_id = course.id
      }
    },
    mainPlayerName (newVal) {
      // Sincronizar mainPlayerName con guest_name solo si es un guest
      if (!this.editedItem.user_id && !this.editedItem.ccslp_user_id) {
        this.editedItem.guest_name = newVal
      }
    },
    userSearch (newVal) {
      // Limpiar timeout anterior
      if (this.userSearchTimeout) {
        clearTimeout(this.userSearchTimeout)
      }

      // Ejecutar búsqueda con debounce
      if (newVal && typeof newVal === 'string' && newVal.length > 0) {
        this.loadingCCSLPUsers = true
        this.userSearchTimeout = setTimeout(() => {
          this.searchCCSLPUsers(newVal)
        }, 500)
      } else {
        // Si el campo está vacío, limpiar resultados de búsqueda
        this.searchedCCSLPUsers = []
        this.loadingCCSLPUsers = false
      }
    },
    newPlayerSearch (newVal) {
      // Limpiar timeout anterior
      if (this.playerSearchTimeout) {
        clearTimeout(this.playerSearchTimeout)
      }

      // Ejecutar búsqueda con debounce
      if (newVal && typeof newVal === 'string' && newVal.length > 0) {
        this.loadingCCSLPUsers = true
        this.playerSearchTimeout = setTimeout(() => {
          this.searchCCSLPUsers(newVal)
        }, 500)
      } else {
        // Si el campo está vacío, limpiar resultados de búsqueda
        this.searchedCCSLPUsers = []
        this.loadingCCSLPUsers = false
      }
    }
  },

  computed: {
    caddieTypes () {
      return [
        { value: 1, text: this.$t('caddie_type_first') },
        { value: 2, text: this.$t('caddie_type_second') },
        { value: 3, text: this.$t('caddie_type_bag') }
      ]
    },
    formTitle () {
      return this.editedIndex === -1 ? this.$t('new_reservation') : this.$t('edit_reservation')
    },
    isStarter () {
      // Only show create reservation button for users with 'starter' permission
      const profile = store.getters.getProfile
      return profile && Array.isArray(profile.permissions) && profile.permissions.includes('starter')
    },
    isLimitedEdit () {
      // Limited edit mode: editing a reservation from allowed past days that has already passed
      if (this.editedIndex === -1) return false
      return this.isDateWithinAllowedRange(this.editedItem.date) && this.isPast(this.editedItem)
    },
    statusOptions () {
      return [
        { value: 1, text: this.$t('pending') },
        { value: 2, text: this.$t('confirmed') },
        { value: 7, text: this.$t('no_show') }
      ]
    },
    minDateAllowed () {
      const maxPastDays = this.getMaximumPastDaysForBooking()
      if (maxPastDays === 0) {
        return this.today
      }
      // Parsear fecha string (YYYY-MM-DD) y restar días
      const [year, month, day] = this.today.split('-').map(Number)
      const minDate = new Date(year, month - 1, day)
      minDate.setDate(minDate.getDate() - maxPastDays)
      return minDate.toISOString().substr(0, 10)
    },

    headers () {
      return [
        { text: this.$t('reservation_date'), value: 'date', align: 'start' },
        { text: this.$t('reservation_time'), value: 'hour' },
        { text: this.$t('holder'), value: 'display_user' },
        { text: this.$t('golf_court'), value: 'course' },
        { text: this.$t('start_hole'), value: 'start_hole' },
        { text: this.$t('hole_count'), value: 'hole_count' },
        { text: this.$t('players'), value: 'invited_people' },
        { text: this.$t('reservation_status'), value: 'status' },
        { text: this.$t('acciones'), value: 'actions', sortable: false, align: 'end' }
      ]
    },
    rules () {
      return {
        required: value => !!value || this.$t('required_field')
      }
    },
    pendingRequestsCount () {
      return this.pendingReservations.length
    },
    cancellationRequestsCount () {
      return this.cancellationReservations.length
    },
    userMatchMap () {
      // Se comenta por rendimiento, tarda mucho en cargar las reservaciones
      /*
      const residentsByAction = {}
      this.users.forEach(u => {
        const actionNum = this.extractActionNumber(u.username)
        if (actionNum) residentsByAction[actionNum] = u
      })

      const map = {}
      this.ccslpUsers.forEach(ccslp => {
        if (!ccslp.socio) return
        const match = residentsByAction[String(ccslp.socio)]
        if (match) map[ccslp.id] = match
      })
      return map
      */

      // ---> BÚSQUEDA REDUCIDA PARA PRUEBAS <---
      const map = {}
      const testSocios = ['35', '35HG', '301']

      this.ccslpUsers.forEach(ccslp => {
        if (!ccslp.socio) return
        const socioStr = String(ccslp.socio)

        if (testSocios.includes(socioStr)) {
          let match = null
          if (socioStr === '35HG') {
            match = this.users.find(u => u.username === 'dep_35_federico_mendizabal')
          } else {
            match = this.users.find(u => {
              const actionNum = this.extractActionNumber(u.username)
              return actionNum === socioStr
            })
          }
          if (match) map[ccslp.id] = match
        }
      })

      return map
    },
    allUsers () {
      const linkedResidentIds = new Set(Object.values(this.userMatchMap).map(u => u.id))

      // Usar usuarios de búsqueda si hay resultados, sino usar los cargados inicialmente
      const ccslpUsersToUse = this.searchedCCSLPUsers.length > 0 ? this.searchedCCSLPUsers : this.ccslpUsers.slice(0, 100)

      const ccslpUsersList = ccslpUsersToUse.map(u => {
        const linkedResident = this.userMatchMap[u.id]
        return {
          ...u,
          type: linkedResident ? 'ccslp_linked' : 'ccslp',
          linkedResidentId: linkedResident ? linkedResident.id : null,
          displaySubtitle: u.socio ? `Socio: ${u.socio}` : '',
          is_moroso: u.is_moroso || false,
          socioNum: u.socio ? parseInt(u.socio, 10) : Infinity
        }
      })

      const systemUsers = this.users
        .filter(u => !linkedResidentIds.has(u.id))
        .filter(u => u.email === 'backend@intrare.app')
        .map(u => ({
          ...u,
          type: 'user',
          displaySubtitle: u.email || u.address || '',
          is_moroso: false,
          socioNum: Infinity
        }))

      // Combinar ambas listas
      const combined = [...systemUsers, ...ccslpUsersList]

      const filtered = combined.filter(user => {
        const name = (user.full_name || '').toLowerCase()
        return !name.includes('prueba') && !name.includes('test')
      })

      // Ordenar según la preferencia seleccionada
      return filtered.sort((a, b) => {
        if (this.sortByAction) {
          if (a.socioNum !== b.socioNum) {
            return a.socioNum - b.socioNum
          }
        }

        const nameA = (a.full_name || '').toLowerCase()
        const nameB = (b.full_name || '').toLowerCase()
        return nameA.localeCompare(nameB)
      })
    },
    timeSlots () {
      // Obtener horarios de apertura y cierre de los cursos de golf
      let startHour = 7
      let startMinute = 0
      let endHour = 14
      let endMinute = 0
      const interval = 10
      const slots = new Set()

      // Usar los horarios de los cursos si están disponibles
      if (this.courts && this.courts.length > 0) {
        const openingTimes = this.courts
          .filter(c => c.opening_time)
          .map(c => {
            const [h, m] = c.opening_time.split(':').map(Number)
            return { hour: h, minute: m }
          })
        const closingTimes = this.courts
          .filter(c => c.closing_time)
          .map(c => {
            const [h, m] = c.closing_time.split(':').map(Number)
            return { hour: h, minute: m }
          })

        if (openingTimes.length > 0) {
          // Encontrar el horario más temprano de apertura
          const earliest = openingTimes.reduce((min, t) =>
            (t.hour < min.hour || (t.hour === min.hour && t.minute < min.minute)) ? t : min
          )
          startHour = earliest.hour
          startMinute = earliest.minute
        }
        if (closingTimes.length > 0) {
          // Encontrar el horario más tarde de cierre
          const latest = closingTimes.reduce((max, t) =>
            (t.hour > max.hour || (t.hour === max.hour && t.minute > max.minute)) ? t : max
          )
          endHour = latest.hour
          endMinute = latest.minute
        }
      }

      // Generar slots desde opening_time hasta closing_time
      for (let h = startHour; h <= endHour; h++) {
        for (let m = 0; m < 60; m += interval) {
          // Saltar minutos antes del inicio en la primera hora
          if (h === startHour && m < startMinute) continue
          // Saltar minutos después del cierre en la última hora
          if (h === endHour && m > endMinute) break
          slots.add(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`)
        }
      }

      // Agregar horarios de reservaciones existentes (por si están fuera del rango)
      this.reservations.forEach(r => {
        if (r.hour) slots.add(r.hour.substring(0, 5))
      })

      return Array.from(slots).sort()
    },
    isWeekend () {
      if (!this.filterDate) return false
      const [year, month, day] = this.filterDate.split('-').map(Number)
      const date = new Date(year, month - 1, day)
      const d = date.getDay()
      return d === 0 || d === 6
    },

    // Mapa de nombres normalizados a IDs para deduplicación robusta
    normalizedMemberMap () {
      const map = new Map()
      // Agregar usuarios del sistema
      this.users.forEach(u => {
        if (u.full_name) {
          map.set(u.full_name.trim().toLowerCase(), { id: u.id, type: 'user' })
        }
      })
      // Agregar usuarios CCSLP
      this.ccslpUsers.forEach(u => {
        if (u.full_name) {
          map.set(u.full_name.trim().toLowerCase(), { id: u.id, type: 'ccslp' })
        } else if (u.name) {
          map.set(u.name.trim().toLowerCase(), { id: u.id, type: 'ccslp' })
        }
      })
      return map
    },

    playerStats () {
      const uniqueResidentIds = new Set()
      const uniqueGuestNames = new Set()

      this.reservations.forEach(r => {
        // 1. Titular (Holder)
        // Socios: user_id (residente intrare), ccslp_user_id (usuario CCSLP) o ccslp_linked (mapeado a user_id)
        const userId = r.user_id || (r.user && r.user.id)
        const ccslpUserId = r.ccslp_user_id || (r.ccslp_user && r.ccslp_user.id)

        if (userId) {
          uniqueResidentIds.add(`user_${userId}`)
        } else if (ccslpUserId) {
          uniqueResidentIds.add(`ccslp_${ccslpUserId}`)
        } else if (r.guest_name) {
          // Sin ID → invitado externo
          uniqueGuestNames.add(r.guest_name.trim().toLowerCase())
        }

        // 2. Invitados (invited_people)
        if (r.invited_people && Array.isArray(r.invited_people)) {
          const invitedUsers = r.invited_users || {}

          // Paso 1: registrar en uniqueResidentIds los socios que sí están en invited_users
          // y construir un set de sus nombres para no procesarlos de nuevo
          const memberNames = new Set()
          Object.values(invitedUsers).forEach(u => {
            if (u.name) memberNames.add(u.name.trim().toLowerCase())
            if (u.user_id && u.type) {
              uniqueResidentIds.add(`${u.type}_${u.user_id}`)
            }
          })

          // Paso 2: para cada nombre en invited_people que NO esté en invited_users,
          // intentar match por nombre en los usuarios cargados en memoria (fallback para
          // reservaciones antiguas o texto libre). Si tampoco se encuentra → invitado externo.
          r.invited_people.forEach(name => {
            if (!name || typeof name !== 'string') return
            const normalized = name.trim().toLowerCase()
            if (memberNames.has(normalized)) return // ya contado vía invited_users

            const memberMatch = this.normalizedMemberMap.get(normalized)
            if (memberMatch) {
              uniqueResidentIds.add(`${memberMatch.type}_${memberMatch.id}`)
            } else {
              uniqueGuestNames.add(normalized)
            }
          })
        }
      })

      return {
        residents: uniqueResidentIds.size,
        guests: uniqueGuestNames.size,
        total: uniqueResidentIds.size + uniqueGuestNames.size
      }
    },
    // Contador de jugadores socios únicos (delegado a playerStats)
    residentPlayersCount () {
      return this.playerStats.residents
    },
    // Contador de jugadores invitados externos únicos (delegado a playerStats)
    guestPlayersCount () {
      return this.playerStats.guests
    },
    // Total de jugadores únicos (delegado a playerStats)
    totalPlayersCount () {
      return this.playerStats.total
    },

    reservationCountByDate () {
      const setsByDate = {}
      this.monthReservations.forEach(r => {
        if (r.status === 4 || r.status === 5 || r.status === 6) return
        if (!r.date) return
        if (!setsByDate[r.date]) setsByDate[r.date] = new Set()
        const userId = r.user_id || (r.user && r.user.id)
        const ccslpUserId = r.ccslp_user_id || (r.ccslp_user && r.ccslp_user.id)
        if (userId) {
          setsByDate[r.date].add(`user_${userId}`)
        } else if (ccslpUserId) {
          setsByDate[r.date].add(`ccslp_${ccslpUserId}`)
        } else if (r.guest_name) {
          setsByDate[r.date].add(`guest_${r.guest_name.trim().toLowerCase()}`)
        }
      })
      const result = {}
      Object.keys(setsByDate).forEach(date => { result[date] = setsByDate[date].size })
      return result
    }
  },

  created () {
    this.initialize()
  },

  beforeDestroy () {
    // Limpiar timeouts para evitar memory leaks
    if (this.userSearchTimeout) {
      clearTimeout(this.userSearchTimeout)
    }
    if (this.playerSearchTimeout) {
      clearTimeout(this.playerSearchTimeout)
    }
  },

  methods: {
    async initialize () {
      // Guardar idHousingUnit en localStorage para persistencia
      const idHousingUnit = store.getters.getProfile.idHousingUnit || localStorage.getItem('idHousingUnit')
      if (idHousingUnit) {
        localStorage.setItem('idHousingUnit', idHousingUnit)
      }

      await this.getCourts()
      await this.getUsers()
      await this.getCCSLPUsers()
      await this.getCaddies()
      await this.getRequests()
      // Las reservaciones del mes se cargan automáticamente por el evento @change del calendario
    },

    getReservationsBySlot (time, hole) {
      return this.reservations.filter(r => {
        const rTime = r.hour ? r.hour.substring(0, 5) : ''
        return rTime === time && r.start_hole === hole
      })
    },

    openDialogWithTime (time, hole) {
      this.editedItem = Object.assign({}, this.defaultItem, {
        invited_people: [],
        invited_users: {},
        caddie_ids: [],
        days_of_week: []
      })
      this.editedItem.date = this.filterDate
      this.editedItem.hour = time
      this.editedItem.start_hole = hole
      // Set the correct course_id based on start_hole
      const course = this.getCourseByStartHole(hole)
      if (course) {
        this.editedItem.course_id = course.id
      }
      this.editedIndex = -1
      this.selectedUser = null
      this.mainPlayerCaddie = null
      this.mainPlayerName = ''
      this.guestCaddies = []
      this.newPlayerName = ''
      this.newPlayerSelection = null
      this.newPlayerSearch = null
      this.dialog = true
    },

    showReservationDetails (item) {
      this.selectedReservation = item
      this.loadAgreementForReservation()
      this.detailsDialog = true
    },

    loadAgreementForReservation () {
      if (!this.selectedReservation || !this.selectedReservation.course) {
        this.selectedAgreement = null
        return
      }

      const courseName = this.getCourseName(this.selectedReservation.course)
      const allAgreements = this.$store.state.courseAgreements?.agreements || []
      this.selectedAgreement = allAgreements.find(
        a => a.name === courseName && a.is_active
      ) || null
    },

    openDownloadModal () {
      this.showDownloadModal = true
    },

    getIdHousingUnit () {
      // Obtener idHousingUnit con prioridad a localStorage después del reload
      return localStorage.getItem('idHousingUnit') || store.getters.getProfile.idHousingUnit
    },

    async getCourts () {
      try {
        const idHousingUnit = this.getIdHousingUnit()
        console.log('Fetching courts for housing unit:', idHousingUnit)
        const response = await this.apiCall('get', '/golf-courses/?housing_unit=' + idHousingUnit)
        this.courts = response.data
        // Set default course_id based on default start_hole (1)
        const defaultCourse = this.courts.find(c => c.hole_range_start === this.defaultItem.start_hole)
        if (defaultCourse) {
          this.defaultItem.course_id = defaultCourse.id
        } else if (this.courts.length > 0) {
          this.defaultItem.course_id = this.courts[0].id
        }
      } catch (error) {
        console.error('Error fetching courts:', error)
      }
    },

    async getUsers () {
      this.loadingUsers = true
      try {
        const response = await this.apiCall('get', '/residents/all')
        const residents = response.data.residents || []
        this.users = residents.map(resident => ({
          id: resident.idUser.id,
          full_name: `${resident.idUser.first_name} ${resident.idUser.last_name}`,
          email: resident.idUser.email,
          address: resident.address,
          type: 'user',
          ...resident.idUser
        }))
      } catch (error) {
        console.error('Error fetching users:', error)
        this.showSnackbar(this.$t('error_fetching_users'), 'error')
      } finally {
        this.loadingUsers = false
      }
    },

    async getCCSLPUsers () {
      const idHousingUnit = this.getIdHousingUnit()
      if (idHousingUnit !== 'a58d8679-e2ae-4055-a243-c63d438f19eb') {
        this.ccslpUsers = []
        return
      }

      this.loadingCCSLPUsers = true
      try {
        // Cargar TODOS los socios paginando para que el conteo de jugadores sea correcto
        const batchSize = 500
        let offset = 0
        let allUsers = []
        let hasMore = true

        while (hasMore) {
          const response = await this.apiCall('get', `/ccslp/users/?limit=${batchSize}&offset=${offset}`)
          const batch = response.data.users || []
          allUsers = allUsers.concat(batch)
          hasMore = batch.length === batchSize
          offset += batchSize
        }

        this.ccslpUsers = allUsers.map(user => ({
          id: user.id,
          full_name: user.name,
          socio: user.socio,
          id_dependiente: user.id_dependiente,
          status: user.status,
          is_moroso: user.is_moroso || false,
          activo: user.activo,
          type: 'ccslp'
        }))
      } catch (error) {
        console.error('Error fetching CCSLP users:', error)
      } finally {
        this.loadingCCSLPUsers = false
      }
    },

    async searchCCSLPUsers (query) {
      const idHousingUnit = this.getIdHousingUnit()
      if (idHousingUnit !== 'a58d8679-e2ae-4055-a243-c63d438f19eb') {
        this.searchedCCSLPUsers = []
        this.loadingCCSLPUsers = false
        return
      }

      try {
        // Buscar por nombre o por socio usando el endpoint
        const response = await this.apiCall('get', `/ccslp/users/?name=${encodeURIComponent(query)}&limit=50&offset=0`)
        const ccslpUsers = response.data.users || []
        this.searchedCCSLPUsers = ccslpUsers.map(user => ({
          id: user.id,
          full_name: user.name,
          socio: user.socio,
          id_dependiente: user.id_dependiente,
          status: user.status,
          is_moroso: user.is_moroso || false,
          activo: user.activo,
          type: 'ccslp'
        }))
      } catch (error) {
        console.error('Error searching CCSLP users:', error)
        this.searchedCCSLPUsers = []
      } finally {
        this.loadingCCSLPUsers = false
      }
    },

    async getCaddies () {
      try {
        const idHousingUnit = this.getIdHousingUnit()
        const response = await this.apiCall('get', '/caddies/?housing_unit=' + idHousingUnit)
        // Ordenar caddies alfabéticamente por nombre
        this.caddies = response.data.sort((a, b) => a.name.localeCompare(b.name))
      } catch (error) {
        console.error('Error fetching caddies:', error)
      }
    },

    async getReservationsForDate (date) {
      this.loading = true
      try {
        const idHousingUnit = this.getIdHousingUnit()
        const response = await this.apiCall('get', `/golf-reservations/by-date?start=${date}&end=${date}&housing_unit=${idHousingUnit}`)
        this.reservations = this.processReservations(response.data)
      } catch (error) {
        console.error('Error fetching reservations:', error)
        this.showSnackbar(this.$t('error_fetching_reservations'), 'error')
      } finally {
        this.loading = false
      }
    },

    setToday () {
      this.focus = ''
    },
    prev () {
      this.$refs.calendar.prev()
    },
    next () {
      this.$refs.calendar.next()
    },
    updateRange ({ start, end }) {
      const [startYear, startMonth] = start.date.split('-').map(Number)

      const today = new Date()
      const currentYear = today.getFullYear()
      const currentMonth = today.getMonth() + 1

      if (startYear === currentYear && startMonth === currentMonth) {
        const monthStart = `${currentYear}-${String(currentMonth).padStart(2, '0')}-01`
        const lastDayDate = new Date(currentYear, currentMonth, 0)
        const monthEnd = `${lastDayDate.getFullYear()}-${String(lastDayDate.getMonth() + 1).padStart(2, '0')}-${String(lastDayDate.getDate()).padStart(2, '0')}`
        this.fetchMonthReservations(monthStart, monthEnd)
      } else {
        const monthStart = start.date.substr(0, 7) + '-01'
        const lastDayDate = new Date(startYear, startMonth, 0)
        const monthEnd = `${lastDayDate.getFullYear()}-${String(lastDayDate.getMonth() + 1).padStart(2, '0')}-${String(lastDayDate.getDate()).padStart(2, '0')}`
        this.fetchMonthReservations(monthStart, monthEnd)
      }
    },
    async fetchMonthReservations (start, end, forceRefresh = false) {
      const cacheKey = start.substring(0, 7) // 'YYYY-MM'
      if (!forceRefresh && this.monthReservationsCache[cacheKey]) {
        this.monthReservations = this.monthReservationsCache[cacheKey]
        return
      }
      try {
        const idHousingUnit = this.getIdHousingUnit()
        const response = await this.apiCall('get', `/golf-reservations/by-date?start=${start}&end=${end}&housing_unit=${idHousingUnit}`)
        this.monthReservations = response.data
        this.monthReservationsCache[cacheKey] = response.data
      } catch (error) {
        console.error('Error fetching month reservations:', error)
      }
    },
    getReservationCount (date) {
      return this.reservationCountByDate[date] || 0
    },
    openDayDialog (date) {
      const dateString = date.date || date
      this.filterDate = dateString
      this.getReservationsForDate(dateString)
      this.dayDialog = true
    },

    async getRequests () {
      this.loadingRequests = true
      try {
        const today = new Date()
        const start = today.toISOString().slice(0, 10)
        const futureDate = new Date(today)
        futureDate.setDate(futureDate.getDate() + 90)
        const end = futureDate.toISOString().slice(0, 10)
        const idHousingUnit = this.getIdHousingUnit()

        const response = await this.apiCall('get', `/golf-reservations/by-date?start=${start}&end=${end}&housing_unit=${idHousingUnit}`)
        const processedData = this.processReservations(response.data)
        // Filter for status 1 (Pending) and 3 (Cancellation Requested)
        this.pendingReservations = processedData.filter(r => r.status === 1)
        this.cancellationReservations = processedData.filter(r => r.status === 3)
      } catch (error) {
        console.error('Error fetching requests:', error)
      } finally {
        this.loadingRequests = false
      }
    },

    onDateChange (date) {
      this.getReservationsForDate(date)
    },

    apiCall (method, endpoint, data = null) {
      const config = {
        method,
        url: `${API_BASE_URL}${endpoint}`,
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': this.language,
          'authorization': 'Token ' + store.getters.getToken
        }
      }

      if (data) {
        config.data = data
      }

      return axios(config)
    },

    getCourseName (course) {
      if (!course) return ''
      if (typeof course === 'object') return course.name
      const court = this.courts.find(c => c.id === course)
      return court ? court.name : course
    },

    getCourseByStartHole (startHole) {
      // Find the course that matches the start_hole based on hole_range_start
      return this.courts.find(c => c.hole_range_start === startHole)
    },

    formatDate (date) {
      if (!date) return ''
      const [year, month, day] = date.split('-')
      return `${day}/${month}/${year}`
    },

    getMaximumPastDaysForBooking () {
      const profile = store.getters.getProfile
      const value = profile?.maxium_past_days_for_booking || 0
      return value
    },

    isDateWithinAllowedRange (date) {
      const maxPastDays = this.getMaximumPastDaysForBooking()
      if (maxPastDays === 0) {
        return date >= this.today
      }

      // Calcular fecha mínima permitida (parsear string YYYY-MM-DD)
      const [year, month, day] = this.today.split('-').map(Number)
      const minDate = new Date(year, month - 1, day)
      minDate.setDate(minDate.getDate() - maxPastDays)
      const minDateStr = minDate.toISOString().substr(0, 10)

      // Comparar strings de fechas (YYYY-MM-DD)
      return date >= minDateStr && date <= this.today
    },

    isPast (item) {
      const now = new Date()
      const resDate = new Date(`${item.date}T${item.hour}`)
      return resDate < now
    },

    isActionDisabled (item, action) {
      // Disable actions for cancelled, rejected, cancelled late, or no-show reservations
      if (item.status === 4 || item.status === 5 || item.status === 6 || item.status === 7) return true

      if (action === 'no_show') {
        return false
      }

      // Allow editing for reservations within allowed past days (limited edit mode)
      if (action === 'edit' && this.isDateWithinAllowedRange(item.date)) {
        return false
      }

      if (this.isPast(item)) return true
      if (item.status === 3 && action === 'edit') return true
      return false
    },

    isToday (date) {
      return date === this.today
    },

    isLimitedEditMode (item) {
      return this.isDateWithinAllowedRange(item.date) && this.isPast(item)
    },

    // Extrae el número de acción de un username con formato ccslp_{accion}_{nombre}
    extractActionNumber (username) {
      if (!username || !username.startsWith('ccslp_')) return null
      const parts = username.split('_')
      return parts.length >= 2 ? parts[1] : null
    },

    filterUsers (item, queryText, itemText) {
      if (!queryText) return true
      const text = queryText.toLowerCase()
      const name = (item.full_name || '').toLowerCase()
      const socio = (item.socio || '').toString().toLowerCase()

      return name.includes(text) || socio.includes(text)
    },

    getStatusColor (status) {
      const colors = {
        1: 'orange',
        2: 'green',
        3: 'purple',
        4: 'blue',
        5: 'grey',
        6: 'deep-orange',
        7: 'red darken-2'
      }
      return colors[status] || 'grey'
    },

    getStatusText (status) {
      const keys = {
        1: 'pending',
        2: 'confirmed',
        3: 'cancellation_requested',
        4: 'cancelled_plus_24h',
        5: 'rejected',
        6: 'cancelled_minus_24h',
        7: 'no_show'
      }
      return this.$t(keys[status] || 'unknown')
    },

    getStatusBaseColor (status) {
      const colors = {
        1: 'orange',
        2: 'green',
        3: 'purple',
        4: 'blue',
        5: 'grey',
        6: 'deep-orange',
        7: 'red'
      }
      return colors[status] || 'grey'
    },

    async changeStatus (item, newStatus) {
      try {
        await this.apiCall('patch', `/golf-reservations/${item.id}/`, { status: newStatus })

        this.getReservationsForDate(this.filterDate)
        if (this.$refs.calendar) {
          const date = new Date(this.focus)
          const firstDay = new Date(date.getFullYear(), date.getMonth(), 1).toISOString().substr(0, 10)
          const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).toISOString().substr(0, 10)
          this.fetchMonthReservations(firstDay, lastDay, true)
        }
        this.getRequests()
      } catch (error) {
        console.error('Error updating status:', error)
        this.showSnackbar(this.$t('error_updating_status'), 'error')
      }
    },

    async handleCancellationRequest (item, action) {
      try {
        const response = await this.apiCall('post', `/golf-reservations/${item.id}/handle-cancellation-request/`, { action })
        const message = response.data?.detail || this.$t('status_updated_success')
        this.showSnackbar(message, 'success')
        this.getReservationsForDate(this.filterDate)
        if (this.$refs.calendar) {
          const date = new Date(this.focus)
          const firstDay = new Date(date.getFullYear(), date.getMonth(), 1).toISOString().substr(0, 10)
          const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).toISOString().substr(0, 10)
          this.fetchMonthReservations(firstDay, lastDay, true)
        }
        this.getRequests()
      } catch (error) {
        console.error('Error handling cancellation request:', error)
        const errorMsg = error.response?.data?.detail || error.response?.data?.message || this.$t('error_updating_status')
        this.showSnackbar(errorMsg, 'error')
      }
    },

    availableCaddies (currentValue) {
      const selectedIds = [this.mainPlayerCaddie, ...this.guestCaddies].filter(id => id)
      return this.caddies.filter(c => !selectedIds.includes(c.id) || c.id === currentValue)
    },

    onUserSelectionChange (val) {
      if (typeof val === 'object' && val !== null) {
        if (val.type === 'ccslp_linked') {
        // CCSLPUser con residente equivalente → crear como residente (transparente para el starter)
          this.editedItem.user_id = val.linkedResidentId
          this.editedItem.ccslp_user_id = null
          this.editedItem.guest_name = ''
          this.mainPlayerName = val.full_name
        } else if (val.type === 'ccslp') {
        // CCSLP User sin equivalente residente
          this.editedItem.ccslp_user_id = val.id
          this.editedItem.user_id = null
          this.editedItem.guest_name = ''
          this.mainPlayerName = val.full_name
        } else {
        // System User/Resident seleccionado directamente
          this.editedItem.user_id = val.id
          this.editedItem.ccslp_user_id = null
          this.editedItem.guest_name = ''
          this.mainPlayerName = val.full_name
        }
      } else if (typeof val === 'string') {
      // Guest name typed
        this.editedItem.user_id = null
        this.editedItem.ccslp_user_id = null
        this.editedItem.guest_name = val
        this.mainPlayerName = val
      } else {
      // Cleared
        this.editedItem.user_id = null
        this.editedItem.ccslp_user_id = null
        this.editedItem.guest_name = ''
        this.mainPlayerName = ''
      }
    },

    updateCaddieIds () {
      const ids = []
      if (this.mainPlayerCaddie) ids.push(this.mainPlayerCaddie)

      this.guestCaddies.forEach(id => {
        if (id) ids.push(id)
      })

      this.editedItem.caddie_ids = ids
    },

    onPlayerSelectionChange (val) {
    // Se llama cuando se selecciona un usuario de la lista
      if (val && typeof val === 'object' && val.full_name) {
        this.addPlayerByName(val.full_name, val)
      }
    },

    addPlayerFromInput () {
    // Se llama cuando se presiona Enter o el botón +
      if (this.editedItem.invited_people.length >= 5) {
        this.showSnackbar(this.$t('max_players_reached'), 'warning')
        return
      }

      let playerName = ''
      let playerData = null

      if (this.newPlayerSelection) {
        if (typeof this.newPlayerSelection === 'object' && this.newPlayerSelection !== null) {
          playerName = this.newPlayerSelection.full_name
          playerData = this.newPlayerSelection
        } else if (typeof this.newPlayerSelection === 'string') {
          playerName = this.newPlayerSelection.trim()
        }
      } else if (this.newPlayerSearch && typeof this.newPlayerSearch === 'string') {
      // Si no hay selección pero hay texto escrito
        playerName = this.newPlayerSearch.trim()
      }

      if (playerName) {
        this.addPlayerByName(playerName, playerData)
      }
    },

    addPlayerByName (name, playerData = null) {
      if (!name || this.editedItem.invited_people.length >= 5) return

      // Formatear nombre con capitalización correcta
      const formattedName = this.capitalizeName(name)

      // Evitar duplicados
      if (this.editedItem.invited_people.includes(formattedName)) {
        this.showSnackbar('Este jugador ya fue agregado', 'warning')
        this.$nextTick(() => {
          this.newPlayerSelection = null
          this.newPlayerSearch = ''
        })
        return
      }

      this.editedItem.invited_people.push(formattedName)
      this.guestCaddies.push(null)

      // Si el jugador es un usuario sincronizado (tiene user_id), agregarlo a invited_users
      if (playerData && (playerData.id || playerData.user_id)) {
        const playerId = playerData.id || playerData.user_id
        const userType = playerData.type || (playerData.id ? 'ccslp' : 'user')
        const playerKey = `${userType}_${playerId}`
        this.editedItem.invited_users[playerKey] = {
          user_id: playerId,
          name: formattedName,
          type: userType
        }
      }

      this.$nextTick(() => {
        this.newPlayerSelection = null
        this.newPlayerSearch = ''
      })
    },

    removePlayer (index) {
      // Obtener el nombre que se va a remover
      const playerName = this.editedItem.invited_people[index]

      this.editedItem.invited_people.splice(index, 1)
      this.guestCaddies.splice(index, 1)

      // Remover del objeto invited_users si existe una entrada correspondiente
      for (const key in this.editedItem.invited_users) {
        if (this.editedItem.invited_users[key].name === playerName) {
          delete this.editedItem.invited_users[key]
          break
        }
      }

      this.updateCaddieIds()
    },

    openDialog () {
      this.editedItem = Object.assign({}, this.defaultItem, {
        invited_people: [],
        invited_users: {},
        caddie_ids: [],
        days_of_week: []
      })
      this.editedItem.date = this.filterDate
      // Set the correct course_id based on default start_hole
      const course = this.getCourseByStartHole(this.editedItem.start_hole)
      if (course) {
        this.editedItem.course_id = course.id
      }
      this.editedIndex = -1
      this.mainPlayerName = ''
      this.selectedUser = null
      this.mainPlayerCaddie = null
      this.guestCaddies = []
      this.newPlayerName = ''
      this.newPlayerSelection = null
      this.newPlayerSearch = null
      this.dialog = true
    },

    editItem (item) {
      this.editedIndex = this.reservations.indexOf(item)
      this.editedItem = Object.assign({}, item)
      this.$set(this.editedItem, 'entry_method', item.entry_method || null)
      if (!Array.isArray(this.editedItem.invited_people)) {
        this.editedItem.invited_people = []
      }
      // Cargar invited_users si existen
      if (!this.editedItem.invited_users || typeof this.editedItem.invited_users !== 'object') {
        this.editedItem.invited_users = {}
      }

      // Asegurar que user_id y ccslp_user_id están correctamente asignados
      this.editedItem.user_id = (item.user && typeof item.user === 'object') ? item.user.id : (item.user_id || null)
      this.editedItem.ccslp_user_id = (item.ccslp_user && typeof item.ccslp_user === 'object') ? item.ccslp_user.id : (item.ccslp_user_id || null)

      // Inicializar mainPlayerName
      this.mainPlayerName = this.getMainPlayerName()
      this.editedItem.course_id = (item.course && typeof item.course === 'object') ? item.course.id : item.course
      this.editedItem.caddie_ids = item.caddies ? item.caddies.map(c => (typeof c === 'object' ? c.id : c)) : (item.caddie ? [item.caddie.id || item.caddie] : [])

      // Initialize caddie distribution
      this.mainPlayerCaddie = null
      this.guestCaddies = []

      if (this.editedItem.caddie_ids && this.editedItem.caddie_ids.length > 0) {
      // Assign first caddie to main player
        this.mainPlayerCaddie = this.editedItem.caddie_ids[0]

        // Assign remaining caddies to guests sequentially
        if (this.editedItem.caddie_ids.length > 1) {
          for (let i = 1; i < this.editedItem.caddie_ids.length; i++) {
          // Ensure array size matches invited people
            if (i - 1 < this.editedItem.invited_people.length) {
              this.$set(this.guestCaddies, i - 1, this.editedItem.caddie_ids[i])
            }
          }
        }
      }

      // Ensure guestCaddies has same length as invited_people
      while (this.guestCaddies.length < this.editedItem.invited_people.length) {
        this.guestCaddies.push(null)
      }

      // Initialize Smart Input - Handle user, ccslp_user, or guest_name
      const userId = item.user_id || (item.user && item.user.id)
      const ccslpUserId = item.ccslp_user_id || (item.ccslp_user && item.ccslp_user.id)

      if (userId) {
      // System user/resident
        if (typeof item.user === 'object' && item.user) {
          this.selectedUser = {
            ...item.user,
            id: item.user.id,
            full_name: `${item.user.first_name || ''} ${item.user.last_name || ''}`.trim() || item.user.email,
            type: 'user'
          }
        } else {
          this.selectedUser = this.users.find(u => u.id === userId) || userId
        }
        this.editedItem.user_id = userId
        this.editedItem.ccslp_user_id = null
      } else if (ccslpUserId) {
      // CCSLP user
        if (typeof item.ccslp_user === 'object' && item.ccslp_user) {
          this.selectedUser = {
            ...item.ccslp_user,
            id: item.ccslp_user.id,
            full_name: item.ccslp_user.name || item.ccslp_user.full_name,
            type: 'ccslp'
          }
        } else {
          this.selectedUser = this.ccslpUsers.find(u => u.id === ccslpUserId) || ccslpUserId
        }
        this.editedItem.ccslp_user_id = ccslpUserId
        this.editedItem.user_id = null
      } else if (this.editedItem.guest_name) {
      // Guest (external)
        this.selectedUser = this.editedItem.guest_name
      } else {
        this.selectedUser = null
      }

      // Limpiar el input de agregar jugadores
      this.newPlayerSelection = null
      this.newPlayerSearch = null

      this.dialog = true
    },

    confirmCancel (item) {
      this.itemToCancel = item
      this.dialogCancel = true
    },

    async cancelItemConfirm () {
      if (this.itemToCancel) {
        try {
          await this.apiCall('delete', `/golf-reservations/${this.itemToCancel.id}/`)
          this.showSnackbar(this.$t('reservation_cancelled_success'), 'success')
          await this.getReservationsForDate(this.filterDate)
          const date = new Date(this.focus)
          const firstDay = new Date(date.getFullYear(), date.getMonth(), 1).toISOString().substr(0, 10)
          const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).toISOString().substr(0, 10)
          await this.fetchMonthReservations(firstDay, lastDay, true)
          await this.getRequests()
        } catch (error) {
          console.error('Error cancelling reservation:', error)
          this.showSnackbar(this.$t('error_cancelling_reservation'), 'error')
        } finally {
          this.closeCancel()
        }
      }
    },

    closeCancel () {
      this.dialogCancel = false
      this.$nextTick(() => {
        this.itemToCancel = null
      })
    },

    confirmNoShow (item) {
      this.itemToNoShow = item
      this.dialogNoShow = true
    },

    async noShowItemConfirm () {
      if (this.itemToNoShow) {
        try {
        // Use status 7 for No Show - this will automatically add a strike
          const payload = {
            status: 7
          }
          await this.apiCall('patch', `/golf-reservations/${this.itemToNoShow.id}/`, payload)
          this.showSnackbar(this.$t('no_show_marked_success'), 'success')
          await this.getReservationsForDate(this.filterDate)
          const date = new Date(this.focus)
          const firstDay = new Date(date.getFullYear(), date.getMonth(), 1).toISOString().substr(0, 10)
          const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).toISOString().substr(0, 10)
          await this.fetchMonthReservations(firstDay, lastDay, true)
          await this.getRequests()
        } catch (error) {
          console.error('Error marking no show:', error)
          const errorMsg = error.response?.data?.detail || error.response?.data?.message || this.$t('error_marking_no_show')
          this.showSnackbar(errorMsg, 'error')
        } finally {
          this.closeNoShow()
        }
      }
    },

    closeNoShow () {
      this.dialogNoShow = false
      this.$nextTick(() => {
        this.itemToNoShow = null
      })
    },

    async onTournamentLinkChange () {
      if (this.linkToTournament) {
        await this.loadAvailableTournaments()
      } else {
        this.editedItem.tournament_id = null
        this.tournamentRegistrations = []
      }
    },

    async loadAvailableTournaments () {
      this.loadingTournaments = true
      try {
        const response = await this.apiCall(
          'get',
          '/tournaments/?status=2'
        )
        this.availableTournaments = response.data.results || response.data || []
      } catch (error) {
        console.error('Error loading tournaments:', error)
        this.showSnackbar('Error al cargar torneos', 'error')
      } finally {
        this.loadingTournaments = false
      }
    },

    async onTournamentSelected () {
      if (!this.editedItem.tournament_id) {
        this.tournamentRegistrations = []
        return
      }

      try {
        const response = await this.apiCall(
          'get',
          `/tournaments/${this.editedItem.tournament_id}/registrations/`
        )
        this.tournamentRegistrations = response.data.registrations || []
      } catch (error) {
        console.error('Error loading tournament registrations:', error)
        this.showSnackbar('Error al cargar jugadores registrados', 'error')
        this.tournamentRegistrations = []
      }
    },

    getRegistrationStatus (status) {
      const statusMap = {
        1: 'Pendiente',
        2: 'Confirmado',
        3: 'Cancelado',
        4: 'No asistió'
      }
      return statusMap[status] || 'Desconocido'
    },

    close () {
      this.dialog = false
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem, {
          invited_people: [],
          invited_users: {},
          caddie_ids: [],
          days_of_week: [],
          tournament_id: null
        })
        this.editedIndex = -1
        this.selectedUser = null
        this.mainPlayerCaddie = null
        this.mainPlayerName = ''
        this.guestCaddies = []
        this.newPlayerName = ''
        this.newPlayerSelection = null
        this.newPlayerSearch = null
        this.linkToTournament = false
        this.tournamentRegistrations = []
        this.$refs.form.resetValidation()
      })
    },

    validateTime () {
      const course = this.courts.find(c => c.id === this.editedItem.course_id)
      if (!course) return true

      const time = this.editedItem.hour
      if (!time) return true

      // Normalizar formatos para comparación (HH:MM vs HH:MM:SS)
      const timeNormalized = time.substring(0, 5)
      const openingNormalized = course.opening_time ? course.opening_time.substring(0, 5) : '00:00'
      const closingNormalized = course.closing_time ? course.closing_time.substring(0, 5) : '23:59'

      if (timeNormalized < openingNormalized || timeNormalized > closingNormalized) {
        this.showSnackbar(`La hora debe estar entre ${openingNormalized} y ${closingNormalized}`, 'error')
        return false
      }
      return true
    },

    async save () {
      if (!this.$refs.form.validate()) return
      if (!this.validateTime()) return

      // Ensure caddie_ids are synced before saving
      this.updateCaddieIds()

      try {
        const payload = {
          course_id: this.editedItem.course_id,
          date: this.editedItem.date,
          hour: this.editedItem.hour,
          start_hole: this.editedItem.start_hole,
          hole_count: this.editedItem.hole_count,
          invited_people: this.editedItem.invited_people,
          invited_users: this.editedItem.invited_users,
          is_recurring: this.editedItem.is_recurring,
          end_date: this.editedItem.end_date,
          days_of_week: this.editedItem.days_of_week
        }

        if (this.editedIndex > -1) {
        // Edit - backend expects 'caddies' for PATCH
          payload.caddies = this.editedItem.caddie_ids
          payload.user_id = this.editedItem.user_id
          payload.guest_name = this.editedItem.guest_name
          // Include status if it was changed (for limited edit mode)
          if (this.editedItem.status) {
            payload.status = this.editedItem.status
          }
          await this.apiCall('patch', `/golf-reservations/${this.editedItem.id}/`, payload)
          if (this.isGuestSelected && this.editedItem.entry_method != null) {
            await this.apiCall('put', `/golf-reservations/${this.editedItem.id}/`, { entry_method: this.editedItem.entry_method })
          }
          this.showSnackbar(this.$t('reservation_updated_success'), 'success')
        } else {
        // Create - backend expects 'caddie_ids' for POST
          payload.caddie_ids = this.editedItem.caddie_ids

          // Add tournament_id if linked to tournament
          if (this.editedItem.tournament_id) {
            payload.tournament_id = this.editedItem.tournament_id
          }

          if (this.editedItem.ccslp_user_id) {
          // Usuario CCSLP
            payload.ccslp_user_id = this.editedItem.ccslp_user_id
            await this.apiCall('post', '/golf-reservations/create-for-ccslp-user/', payload)
          } else if (this.editedItem.user_id) {
          // Residente (usuario del sistema)
            payload.user_id = this.editedItem.user_id
            await this.apiCall('post', '/golf-reservations/create-for-user/', payload)
          } else if (this.isGuestSelected() || this.editedItem.guest_name) {
          // Invitado no registrado
            payload.entry_method = this.editedItem.entry_method
            payload.guest_name = this.editedItem.guest_name
            await this.apiCall('post', '/golf-reservations/create-for-guest/', payload)
          }
          this.showSnackbar(this.$t('reservation_created_success'), 'success')
        }

        const reservationDate = this.editedItem.date
        this.close()
        // Actualizar filterDate al día de la reservación creada/editada
        this.filterDate = reservationDate
        await this.getReservationsForDate(reservationDate)
        if (this.$refs.calendar) {
          const date = new Date(this.focus)
          const firstDay = new Date(date.getFullYear(), date.getMonth(), 1).toISOString().substr(0, 10)
          const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).toISOString().substr(0, 10)
          await this.fetchMonthReservations(firstDay, lastDay, true)
        }
        this.getRequests()
      } catch (error) {
        console.error('Error saving reservation:', error)
        let errorMsg = this.$t('error_saving_reservation')
        if (error.response?.data) {
          const data = error.response.data

          // Si data es un array directamente (ej: ["mensaje de error"])
          if (Array.isArray(data) && data.length > 0) {
            errorMsg = data[0]
          } else if (data.detail) {
            // Si hay un campo 'detail'
            if (Array.isArray(data.detail)) {
              errorMsg = data.detail[0]
            } else {
              errorMsg = data.detail
            }
          } else if (data.message) {
            // Si hay un campo 'message'
            if (Array.isArray(data.message)) {
              errorMsg = data.message[0]
            } else {
              errorMsg = data.message
            }
          } else if (data.error) {
            // Si hay un campo 'error'
            if (Array.isArray(data.error)) {
              errorMsg = data.error[0]
            } else {
              errorMsg = data.error
            }
          } else if (data.non_field_errors && Array.isArray(data.non_field_errors)) {
            // Errores no asociados a un campo específico
            errorMsg = data.non_field_errors[0]
          } else if (typeof data === 'object') {
            // Buscar el primer error en cualquier campo
            const firstKey = Object.keys(data)[0]
            if (firstKey && data[firstKey]) {
              if (Array.isArray(data[firstKey])) {
                errorMsg = data[firstKey][0]
              } else {
                errorMsg = data[firstKey]
              }
            }
          }
        }
        this.showSnackbar(errorMsg, 'error')
      }
    },

    showSnackbar (text, color) {
      this.snackbarText = text
      this.snackbarColor = color
      this.snackbar = true
    },

    getUserName (user) {
      if (!user) return ''
      if (typeof user === 'object') {
        if (user.first_name || user.last_name) {
          return `${user.first_name || ''} ${user.last_name || ''}`.trim()
        }
        return user.email || ''
      }
      const found = this.users.find(u => u.id === user)
      return found ? found.full_name : user
    },

    processReservations (data) {
      return data.map(r => {
        let displayUser = r.user_display
        if (!displayUser) {
          if (r.user) {
            displayUser = this.getUserName(r.user)
          } else if (r.ccslp_user) {
            displayUser = r.ccslp_user.name || 'Usuario CCSLP'
          } else {
            displayUser = r.guest_name || 'Invitado'
          }
        }

        const residentId = r.user_id || (r.user && r.user.id)
        const linkedSocio = residentId ? this.getLinkedSocioByResidentId(residentId) : null
        if (linkedSocio && typeof displayUser === 'string') {
          const hasSocioAlready = /\(\d+\)\s*$/.test(displayUser)
          if (!hasSocioAlready) {
            displayUser = `${displayUser} (${linkedSocio})`
          }
        }

        return {
          ...r,
          display_user: displayUser
        }
      })
    },

    getLinkedSocioByResidentId (residentId) {
      if (!residentId) return null

      for (const ccslp of this.ccslpUsers) {
        const linkedResident = this.userMatchMap[ccslp.id]
        if (linkedResident && linkedResident.id === residentId) {
          return ccslp.socio ? String(ccslp.socio) : null
        }
      }

      return null
    },

    getInitials (name) {
      if (!name) return ''
      const parts = name.split(' ')
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    },

    isMember (reservation) {
      return !!(
        reservation.user_id ||
        (reservation.user && reservation.user.id) ||
        reservation.ccslp_user_id ||
        (reservation.ccslp_user && reservation.ccslp_user.id)
      )
    },

    isTournamentReservation (reservation) {
      return !!reservation.tournament
    },

    capitalizeName (name) {
      if (!name) return ''
      let cleanName = name.replace(/\s*\(\d+\)\s*$/, '').replace(/^\d+\s*-\s*/, '').trim()
      return cleanName
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },

    formatDisplayName (name, preserveSocio = false) {
    // En titular puede conservarse el número de acción; en invitados se mantiene el formato previo.
      if (!name) return ''

      const socioMatch = preserveSocio ? name.match(/\((\d+)\)\s*$/) : null
      const formattedName = this.capitalizeName(name)

      if (socioMatch) {
        return `${formattedName} (${socioMatch[1]})`
      }

      return formattedName
    },

    getMainPlayerName () {
    // Obtener el nombre del jugador principal según el tipo de usuario
      if (this.editedItem.user_id) {
        // Primero intenta obtener del objeto anidado (si viene del backend)
        if (this.editedItem.user && typeof this.editedItem.user === 'object') {
          const fullName = `${this.editedItem.user.first_name || ''} ${this.editedItem.user.last_name || ''}`.trim()
          return fullName || this.editedItem.user.email || ''
        }
        // Si no, busca en el array de usuarios cargados
        const user = this.users.find(u => u.id === this.editedItem.user_id)
        return user ? user.full_name : ''
      } else if (this.editedItem.ccslp_user_id) {
        // Primero intenta obtener del objeto anidado (si viene del backend)
        if (this.editedItem.ccslp_user && typeof this.editedItem.ccslp_user === 'object') {
          const name = this.editedItem.ccslp_user.name || this.editedItem.ccslp_user.full_name || ''
          if (name) return name
        }
        // Si no está en ccslp_user, intenta usar display_user del backend
        if (this.editedItem.display_user) {
          return this.editedItem.display_user
        }
        // Si no, busca en el array de usuarios CCSLP cargados
        const ccslpUser = this.ccslpUsers.find(u => u.id === this.editedItem.ccslp_user_id)
        return ccslpUser ? ccslpUser.full_name : ''
      } else {
        return this.editedItem.guest_name || ''
      }
    },

    getCaddieName (caddieId) {
      const caddie = this.caddies.find(c => c.id === caddieId)
      return caddie ? caddie.name : caddieId
    },

    getRecessTime (startTime) {
      if (!startTime) return ''
      const [h, m] = startTime.split(':').map(Number)
      let rh = h + 2
      let rm = m + 39
      if (rm >= 60) {
        rh += 1
        rm -= 60
      }
      return `${rh.toString().padStart(2, '0')}:${rm.toString().padStart(2, '0')}`
    },

    openNewCaddieDialog (context) {
      this.caddieCreationContext = context
      this.newCaddie = { name: '', caddie_type: null }
      if (this.$refs.formNewCaddie) {
        this.$refs.formNewCaddie.resetValidation()
      }
      this.dialogNewCaddie = true
    },

    closeNewCaddieDialog () {
      this.dialogNewCaddie = false
    },

    async saveNewCaddie () {
      if (!this.$refs.formNewCaddie.validate()) return

      this.loadingNewCaddie = true
      try {
        const idHousingUnit = store.getters.getProfile.idHousingUnit || localStorage.getItem('idHousingUnit')
        const caddieData = { ...this.newCaddie, IdHousingUnit: idHousingUnit }
        const response = await this.apiCall('post', '/caddies/', caddieData)
        const createdCaddie = response.data
        this.caddies.push(createdCaddie)

        if (this.caddieCreationContext === -1) {
          this.mainPlayerCaddie = createdCaddie.id
        } else if (this.caddieCreationContext >= 0) {
          this.$set(this.guestCaddies, this.caddieCreationContext, createdCaddie.id)
        }
        this.updateCaddieIds()
        this.showSnackbar(this.$t('caddie_created_success') || 'Caddie creado exitosamente', 'success')
        this.closeNewCaddieDialog()
      } catch (error) {
        console.error('Error creating caddie:', error)
        this.showSnackbar(this.$t('error_creating_caddie') || 'Error al crear caddie', 'error')
      } finally {
        this.loadingNewCaddie = false
      }
    },
    entryMethodOptions () {
      return [
        { value: 1, text: this.$t('entry_method_pago') },
        { value: 2, text: this.$t('entry_method_convenio') },
        { value: 3, text: this.$t('entry_method_premio') }
      ]
    },
    isGuestSelected () {
      return !this.editedItem.user_id && !this.editedItem.ccslp_user_id && !!this.editedItem.guest_name
    }
  }
}

</script>

<style scoped>
.tee-sheet-table th {
  text-transform: uppercase;
  letter-spacing: 1px;
  font-size: 0.9rem !important;
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: #1976d2 !important;
}

.tee-row:hover td {
  background-color: #f5f5f5;
}

.cell-hover:hover .add-btn-container {
  opacity: 1 !important;
}

.add-btn-container:hover {
  background-color: rgba(0, 0, 0, 0.05);
  cursor: pointer;
}

.clickable-card {
  cursor: pointer;
}

/* Estilos para diferenciar socios e invitados */
.member-card {
  border-top: 2px solid #1976d2 !important;
}

.guest-card {
  border-top: 2px solid #ff9800 !important;
}

/* Reservaciones vinculadas a un torneo */
.tournament-card {
  border-top: 2px solid #9c27b0 !important;
  background-color: rgba(156, 39, 176, 0.06) !important;
}

/* Scroll horizontal para chips con indicador visual */
.chips-scroll-container {
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  padding-bottom: 8px;
  margin-right: -8px;
  padding-right: 8px;
  /* Sombra sutil que indica más contenido */
  mask-image: linear-gradient(to right, black 90%, transparent 100%);
  -webkit-mask-image: linear-gradient(to right, black 90%, transparent 100%);
}

/* Barra de scroll delgada y estilizada */
.chips-scroll-container::-webkit-scrollbar {
  height: 4px;
}

.chips-scroll-container::-webkit-scrollbar-track {
  background: #e0e0e0;
  border-radius: 4px;
}

.chips-scroll-container::-webkit-scrollbar-thumb {
  background: #9e9e9e;
  border-radius: 4px;
}

.chips-scroll-container::-webkit-scrollbar-thumb:hover {
  background: #757575;
}

/* Firefox */
.chips-scroll-container {
  scrollbar-width: thin;
  scrollbar-color: #9e9e9e #e0e0e0;
}
</style>
